/**
 * HighlightService — safe HTML highlighting helpers.
 *
 * Walks text nodes only and wraps matches in <mark> elements. Never touches
 * attributes or tag content (so we can't accidentally inject classes onto
 * <img src="…">, etc.).
 *
 * Usage:
 *   const highlighted = HighlightService.highlightHtml(cleanHtml, {
 *     terms: ["create", "client"],   // user's query words
 *     span: "Click the relevant type from the above",  // AI's best span
 *   });
 *
 * Output is HTML safe to dangerouslySetInnerHTML. The input is expected to be
 * already-sanitized (DOMPurify on the caller's side) — this service only adds
 * <mark> wrappers, it does NOT sanitize.
 */

const MIN_TERM_LEN = 3;
// Tags whose text content should NEVER be highlighted (would break code/links/etc).
// Notably MARK is NOT here — we want per-term highlights to nest INSIDE an AI
// best-span mark of a different class. The same-class dedup happens further
// down inside the walker callback.
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "A", "CODE", "PRE", "TEXTAREA", "INPUT"]);

const STOP_WORDS = new Set([
	"the", "and", "for", "but", "with", "that", "this", "from", "into", "have",
	"has", "had", "are", "was", "were", "you", "your", "how", "what", "when",
	"where", "why", "can", "will", "would", "could", "should",
]);

function _escapeForRegex(s) {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function _normalizeTerms(terms) {
	if (!Array.isArray(terms)) return [];
	const seen = new Set();
	const out = [];
	for (const t of terms) {
		if (typeof t !== "string") continue;
		const word = t.trim();
		if (word.length < MIN_TERM_LEN) continue;
		const key = word.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(word);
	}
	return out;
}

/**
 * Walk text nodes inside `root` and wrap any match for `pattern` in
 * <mark class="markClass">…</mark>. Operates IN-PLACE.
 */
function _wrapMatches(root, pattern, markClass) {
	if (!pattern) return;
	const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode(node) {
			const parent = node.parentNode;
			if (!parent) return NodeFilter.FILTER_REJECT;
			if (SKIP_TAGS.has(parent.nodeName)) return NodeFilter.FILTER_REJECT;
			// Skip already-marked spans of the SAME class so we don't double-wrap.
			if (parent.nodeName === "MARK" && parent.classList.contains(markClass)) {
				return NodeFilter.FILTER_REJECT;
			}
			return node.nodeValue && pattern.test(node.nodeValue)
				? NodeFilter.FILTER_ACCEPT
				: NodeFilter.FILTER_REJECT;
		},
	});

	const toReplace = [];
	let n = walker.nextNode();
	while (n) {
		toReplace.push(n);
		n = walker.nextNode();
	}

	for (const textNode of toReplace) {
		const text = textNode.nodeValue;
		// Reset regex state for each invocation since `pattern` may be global.
		pattern.lastIndex = 0;
		const frag = root.ownerDocument.createDocumentFragment();
		let lastIdx = 0;
		let m;
		while ((m = pattern.exec(text)) !== null) {
			const start = m.index;
			const end = pattern.lastIndex;
			if (start === end) {
				// zero-length match — guard against infinite loop
				pattern.lastIndex += 1;
				continue;
			}
			if (start > lastIdx) {
				frag.appendChild(root.ownerDocument.createTextNode(text.slice(lastIdx, start)));
			}
			const mark = root.ownerDocument.createElement("mark");
			mark.className = markClass;
			mark.appendChild(root.ownerDocument.createTextNode(text.slice(start, end)));
			frag.appendChild(mark);
			lastIdx = end;
		}
		if (lastIdx < text.length) {
			frag.appendChild(root.ownerDocument.createTextNode(text.slice(lastIdx)));
		}
		textNode.parentNode.replaceChild(frag, textNode);
	}
}

const HighlightService = {
	/**
	 * Highlight query terms and an AI best-span inside a sanitized HTML string.
	 * Returns a new HTML string ready for dangerouslySetInnerHTML.
	 *
	 * @param {string} html — sanitized HTML (NOT raw user input)
	 * @param {Object} opts
	 * @param {string[]} opts.terms — query words to soft-highlight
	 * @param {string|null} opts.span — AI's best matching span to strong-highlight
	 * @returns {string}
	 */
	highlightHtml(html, { terms = [], span = null } = {}) {
		if (!html || typeof html !== "string") return html || "";

		const doc = document.implementation.createHTMLDocument("hl");
		const root = doc.body;
		root.innerHTML = html;

		// 1) Strong: AI best span first (so subsequent term highlights nest inside it
		//    rather than fragmenting it). Match case-insensitively; the span text may
		//    appear with slightly different surrounding whitespace, so collapse runs
		//    of whitespace in the pattern.
		if (span && typeof span === "string" && span.trim().length >= 8) {
			const flexible = _escapeForRegex(span.trim()).replace(/\s+/g, "\\s+");
			try {
				const spanRe = new RegExp(flexible, "gi");
				_wrapMatches(root, spanRe, "rte-hl-span");
			} catch (e) {
				// Bad regex — silently skip the span highlight
			}
		}

		// 2) Soft: per-term highlights with word boundaries to avoid partial-word matches.
		const cleanTerms = _normalizeTerms(terms);
		if (cleanTerms.length) {
			const joined = cleanTerms.map(_escapeForRegex).join("|");
			try {
				const termRe = new RegExp("\\b(?:" + joined + ")\\b", "gi");
				_wrapMatches(root, termRe, "rte-hl-term");
			} catch (e) {
				// ignore
			}
		}

		return root.innerHTML;
	},

	/**
	 * Convenience: split a query string into term-sized words suitable for `terms`.
	 * Strips stop-words and tokens shorter than the threshold.
	 *
	 * @param {string} query
	 * @returns {string[]}
	 */
	termsFromQuery(query) {
		if (!query || typeof query !== "string") return [];
		return query
			.toLowerCase()
			.split(/[^a-z0-9]+/)
			.filter(t => t && t.length >= MIN_TERM_LEN && !STOP_WORDS.has(t));
	},
};

export default HighlightService;
